import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import InvoiceListView from "@/components/InvoiceListView";
import { api, formatRupiah } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { Plus, Trash2, Printer, Download, Save, FileText, ArrowLeft, Loader2, Eye, ImagePlus, X, Share2, ChevronDown, Settings2 } from "lucide-react";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

const today = () => new Date().toISOString().slice(0, 10);
const EMPTY = () => ({
  id: null, invoice_number: "", buyer_name: "", buyer_address: "", date: today(),
  items: [{ name: "", qty: 1, price: 0 }], shipping_cost: 0, shipping_via: "", shipping_resi: "", shipping_date: "",
  shipping_status: "menunggu",
  payment_method: "Transfer Bank\nBCA 515-046-1300\nKIKI DARMAWAN",
  terms: "1. Harap lakukan transfer sebelum 2x24 Jam\n2. Jika lebih dari itu maka order kami anggap cancel\n3. Produk akan dikirim setelah pelunasan kami terima.\n4. Harap konfirmasi setelah transfer, sertakan foto bukti transfer.\nTerimakasih",
  payments: [], signature_name: "Kiki darmawan", logo_url: "", signature_url: "", theme_color: "",
});
const fmtDate = (d) => { try { return new Date(d).toLocaleDateString("id-ID", { day: "2-digit", month: "short", year: "numeric" }); } catch { return d; } };
const SHIP_STATUS = [
  { v: "menunggu", label: "Menunggu Pembayaran", cls: "bg-yellow-100 text-yellow-800" },
  { v: "diproses", label: "Diproses", cls: "bg-blue-100 text-blue-700" },
  { v: "dikirim", label: "Sudah Dikirim", cls: "bg-emerald-100 text-emerald-700" },
];
const shipLabel = (v) => (SHIP_STATUS.find((s) => s.v === v) || SHIP_STATUS[0]).label;
const PAY_LABEL = { unpaid: "Belum Bayar", dp: "DP / Sebagian", paid: "Lunas" };
const THEME_PRESETS = ["#3b5bdb", "#00133F", "#F0005A", "#0d9488", "#b45309", "#7c3aed"];

async function uploadImage(file) {
  const fd = new FormData();
  fd.append("file", file);
  const res = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
  return `${api.defaults.baseURL}/files/${res.data.file_id}`;
}

function ImgPick({ label, value, onChange, testid }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try { onChange(await uploadImage(f)); toast.success(`${label} terunggah`); }
    catch { toast.error(`Gagal mengunggah ${label.toLowerCase()}`); }
    finally { setBusy(false); e.target.value = ""; }
  };
  return (
    <div>
      <Label className="text-xs">{label}</Label>
      <div className="mt-1 flex items-center gap-2">
        {value ? (
          <div className="relative">
            <img src={value} alt={label} className="h-14 w-24 object-contain rounded-lg border border-slate-200 bg-white" data-testid={`${testid}-preview`} />
            <button onClick={() => onChange("")} className="absolute -top-1.5 -right-1.5 bg-white border border-slate-200 rounded-full p-0.5 text-slate-400 hover:text-red-500" data-testid={`${testid}-remove`}><X className="w-3 h-3" /></button>
          </div>
        ) : (
          <button type="button" onClick={() => ref.current?.click()} disabled={busy}
            className="h-14 w-24 rounded-lg border-2 border-dashed border-slate-300 text-slate-400 hover:border-[#F0005A] hover:text-[#F0005A] flex flex-col items-center justify-center gap-0.5 text-[10px]"
            data-testid={testid}>
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImagePlus className="w-4 h-4" />} Unggah
          </button>
        )}
      </div>
      <input ref={ref} type="file" accept="image/*" className="hidden" onChange={pick} />
    </div>
  );
}

function InvoiceDoc({ inv, store, defLogo, accent, gset }) {
  const tc = inv.theme_color || gset?.theme_color || accent || "#3b5bdb";
  const subtotal = inv.items.reduce((s, it) => s + (Number(it.qty) || 0) * (Number(it.price) || 0), 0);
  const grandTotal = subtotal + (Number(inv.shipping_cost) || 0);
  const paidTotal = (inv.payments || []).reduce((s, p) => s + (Number(p.amount) || 0), 0) || (Number(inv.paid_amount) || 0);
  const balance = Math.max(0, grandTotal - paidTotal);
  const logoSrc = inv.logo_url || gset?.logo_url || defLogo;
  const sigSrc = inv.signature_url || gset?.signature_url;
  const ship = SHIP_STATUS.find((s) => s.v === inv.shipping_status) || SHIP_STATUS[0];

  return (
    <div id="invoice-print" className="bg-white shadow-lg p-8 text-[#0f172a]" style={{ width: "210mm", minHeight: "297mm" }} data-testid="invoice-preview">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold tracking-wide" style={{ color: tc }}>INVOICE</h1>
        {inv.shipping_status === "dikirim" && <span className={`text-xs font-semibold px-3 py-1 rounded-full ${ship.cls}`} data-testid="doc-ship-status">{ship.label}</span>}
      </div>
      <div className="flex justify-between items-start mt-4">
        <div className="text-sm leading-snug">
          <p className="font-bold text-base">{store.name}</p>
          <p className="whitespace-pre-line text-slate-600">{store.address}</p>
          {store.phone && <p className="text-slate-600">Mobile: {store.phone}</p>}
        </div>
        {logoSrc ? <img src={logoSrc} alt="logo" crossOrigin="anonymous" className="w-28 h-auto object-contain" /> : <div className="w-20 h-20 rounded-xl text-white flex items-center justify-center text-3xl font-black" style={{ background: tc }}>R</div>}
      </div>
      <div className="border-t-2 my-4" style={{ borderColor: tc }} />
      <div className="flex justify-between items-start">
        <div className="text-sm leading-snug">
          <p className="font-bold">Kepada</p>
          <p className="font-medium">{inv.buyer_name || "—"}</p>
          <p className="whitespace-pre-line text-slate-600">{inv.buyer_address}</p>
        </div>
        <div className="text-sm text-right">
          <p className="font-bold text-base">Invoice No :</p>
          <p className="font-bold" style={{ color: tc }}>{inv.invoice_number || "(otomatis saat simpan)"}</p>
          <p className="mt-1">Tanggal : {fmtDate(inv.date)}</p>
        </div>
      </div>

      <table className="w-full text-sm mt-5">
        <thead><tr className="text-white text-left" style={{ background: tc }}><th className="p-2 font-semibold">Tgl Pengiriman</th><th className="p-2 font-semibold">Ongkos Kirim</th><th className="p-2 font-semibold">Kirim Via</th><th className="p-2 font-semibold">Resi</th></tr></thead>
        <tbody><tr><td className="p-2">{inv.shipping_date ? fmtDate(inv.shipping_date) : "-"}</td><td className="p-2">{formatRupiah(inv.shipping_cost)}</td><td className="p-2">{inv.shipping_via || "-"}</td><td className="p-2">{inv.shipping_resi || "-"}</td></tr></tbody>
      </table>

      <table className="w-full text-sm mt-3">
        <thead><tr className="text-white text-left" style={{ background: tc }}><th className="p-2 font-semibold">Produk</th><th className="p-2 font-semibold text-center w-16">Qty</th><th className="p-2 font-semibold text-right w-32">Harga</th><th className="p-2 font-semibold text-right w-32">Jumlah</th></tr></thead>
        <tbody>
          {inv.items.filter((it) => it.name.trim()).map((it, i) => (
            <tr key={i} className="border-b border-slate-100"><td className="p-2">{it.name}</td><td className="p-2 text-center">{it.qty}</td><td className="p-2 text-right">{formatRupiah(it.price)}</td><td className="p-2 text-right">{formatRupiah(it.qty * it.price)}</td></tr>
          ))}
        </tbody>
      </table>

      <div className="flex justify-between mt-4 text-sm gap-6">
        <div className="whitespace-pre-line text-slate-700"><p className="font-bold">Cara Pembayaran</p>{inv.payment_method}</div>
        <div className="w-64 shrink-0">
          <div className="flex justify-between py-1"><span>Subtotal</span><span className="font-medium">{formatRupiah(subtotal)}</span></div>
          <div className="flex justify-between py-1"><span>Ongkos Kirim</span><span className="font-medium">{formatRupiah(inv.shipping_cost)}</span></div>
          <div className="flex justify-between py-1 border-t border-slate-200 font-bold"><span>Total Tagihan</span><span>{formatRupiah(grandTotal)}</span></div>
          <div className="flex justify-between py-1 text-slate-600"><span>Total Dibayar</span><span>{formatRupiah(paidTotal)}</span></div>
          <div className="flex justify-between py-1 border-t border-slate-200 font-bold"><span>Sisa Tagihan</span><span style={{ color: balance === 0 ? "#059669" : tc }}>{formatRupiah(balance)}</span></div>
          <p className="text-right text-xs mt-1 font-semibold" style={{ color: tc }}>{PAY_LABEL[paidTotal <= 0 ? "unpaid" : paidTotal < grandTotal ? "dp" : "paid"]}</p>
        </div>
      </div>

      {(inv.payments || []).filter((p) => p.label.trim() || Number(p.amount) > 0).length > 0 && (
        <div className="mt-5">
          <p className="text-sm font-bold mb-1">Riwayat Pembayaran</p>
          <table className="w-full text-sm">
            <thead><tr className="text-white text-left" style={{ background: tc }}><th className="p-2 font-semibold">Keterangan</th><th className="p-2 font-semibold">Tanggal</th><th className="p-2 font-semibold text-right w-32">Jumlah</th></tr></thead>
            <tbody>
              {inv.payments.filter((p) => p.label.trim() || Number(p.amount) > 0).map((p, i) => (
                <tr key={i} className="border-b border-slate-100"><td className="p-2">{p.label || "-"}</td><td className="p-2">{p.date ? fmtDate(p.date) : "-"}</td><td className="p-2 text-right">{formatRupiah(p.amount)}</td></tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="flex justify-between items-end mt-8">
        <div className="text-xs whitespace-pre-line text-slate-600 max-w-sm"><p className="font-bold mb-1">Syarat dan Ketentuan</p>{inv.terms}</div>
        <div className="text-center">
          <div className="h-16 flex items-end justify-center">
            {sigSrc
              ? <img src={sigSrc} alt="tanda tangan" crossOrigin="anonymous" className="h-16 object-contain" />
              : <span className="italic text-2xl text-slate-700" style={{ fontFamily: "cursive" }}>{inv.signature_name}</span>}
          </div>
          <div className="border-t border-slate-400 w-48 mt-1" />
          <p className="text-sm font-medium mt-1">{inv.signature_name}</p>
        </div>
      </div>
    </div>
  );
}

export default function InvoiceManager() {
  const [list, setList] = useState([]);
  const [inv, setInv] = useState(EMPTY());
  const [products, setProducts] = useState([]);
  const [store, setStore] = useState({ name: "Rooftop Print", address: "", phone: "" });
  const [logo, setLogo] = useState("");
  const [accent, setAccent] = useState("");
  const [gset, setGset] = useState({ logo_url: "", signature_url: "", theme_color: "" });
  const [savingGset, setSavingGset] = useState(false);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(false);
  const [sp, setSp] = useSearchParams();
  const editId = sp.get("id");
  const isEditor = sp.get("edit") === "new" || !!editId;

  const load = () => api.get("/admin/invoices").then((r) => setList(r.data)).catch(() => {});
  useEffect(() => {
    load();
    api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
    api.get("/settings/sender").then((r) => setStore({ name: r.data.name || "Rooftop Print", address: r.data.address || "", phone: r.data.phone || "" })).catch(() => {});
    api.get("/settings/logo").then((r) => setLogo(r.data.logo_url || "")).catch(() => {});
    api.get("/settings/storefront").then((r) => setAccent(r.data?.theme?.accent || "")).catch(() => {});
    api.get("/settings/invoice").then((r) => setGset({ logo_url: "", signature_url: "", theme_color: "", ...(r.data || {}) })).catch(() => {});
  }, []);

  useEffect(() => {
    if (!editId || inv.id === editId) { return; }
    api.get(`/admin/invoices/${editId}`).then((r) => editInvoice(r.data)).catch(() => toast.error("Invoice tidak ditemukan"));
  }, [editId]); // eslint-disable-line

  const subtotal = inv.items.reduce((s, it) => s + (Number(it.qty) || 0) * (Number(it.price) || 0), 0);
  const grandTotal = subtotal + (Number(inv.shipping_cost) || 0);
  const paidTotal = inv.payments.reduce((s, p) => s + (Number(p.amount) || 0), 0);
  const balance = Math.max(0, grandTotal - paidTotal);
  const payStatus = paidTotal <= 0 ? "unpaid" : paidTotal < grandTotal ? "dp" : "paid";

  const setItem = (i, k, v) => setInv((f) => ({ ...f, items: f.items.map((it, j) => j === i ? { ...it, [k]: v } : it) }));
  const onItemName = (i, name) => {
    const p = products.find((x) => x.name === name);
    setInv((f) => ({ ...f, items: f.items.map((it, j) => j === i ? { ...it, name, price: p ? (p.promo_price && p.promo_price < p.base_price ? p.promo_price : p.base_price) : it.price } : it) }));
  };
  const addItem = () => setInv((f) => ({ ...f, items: [...f.items, { name: "", qty: 1, price: 0 }] }));
  const removeItem = (i) => setInv((f) => ({ ...f, items: f.items.filter((_, j) => j !== i) }));
  const setPay = (i, k, v) => setInv((f) => ({ ...f, payments: f.payments.map((p, j) => j === i ? { ...p, [k]: v } : p) }));
  const addPay = () => setInv((f) => ({ ...f, payments: [...f.payments, { label: f.payments.length === 0 ? "DP" : "Pelunasan", amount: 0, date: today() }] }));
  const removePay = (i) => setInv((f) => ({ ...f, payments: f.payments.filter((_, j) => j !== i) }));
  const set = (k, v) => setInv((f) => ({ ...f, [k]: v }));

  const payload = () => ({
    buyer_name: inv.buyer_name, buyer_address: inv.buyer_address, date: inv.date,
    items: inv.items.filter((it) => it.name.trim()).map((it) => ({ name: it.name.trim(), qty: Number(it.qty) || 0, price: Number(it.price) || 0 })),
    shipping_cost: Number(inv.shipping_cost) || 0, shipping_via: inv.shipping_via, shipping_resi: inv.shipping_resi, shipping_date: inv.shipping_date,
    shipping_status: inv.shipping_status,
    payment_method: inv.payment_method, terms: inv.terms,
    payments: inv.payments.filter((p) => p.label.trim() || Number(p.amount) > 0).map((p) => ({ label: p.label.trim(), amount: Number(p.amount) || 0, date: p.date || "" })),
    paid_amount: paidTotal, payment_status: payStatus, signature_name: inv.signature_name,
    logo_url: inv.logo_url, signature_url: inv.signature_url, theme_color: inv.theme_color,
  });

  const save = async () => {
    if (!inv.buyer_name.trim()) { toast.error("Nama pembeli wajib diisi"); return; }
    setSaving(true);
    try {
      const res = inv.id ? await api.put(`/admin/invoices/${inv.id}`, payload()) : await api.post("/admin/invoices", payload());
      setInv((f) => ({ ...f, id: res.data.id, invoice_number: res.data.invoice_number }));
      if (!inv.id) setSp({ id: res.data.id }, { replace: true });
      toast.success(inv.id ? "Invoice diperbarui" : "Invoice dibuat & tersimpan");
      load();
    } catch (e) { toast.error("Gagal menyimpan invoice"); } finally { setSaving(false); }
  };
  const editInvoice = (x) => setInv({ ...EMPTY(), ...x, items: x.items?.length ? x.items : [{ name: "", qty: 1, price: 0 }], payments: x.payments || [], shipping_status: x.shipping_status || (x.shipped ? "dikirim" : "menunggu") });
  const newInvoice = () => window.open("/admin/invoice?edit=new", "_blank", "noopener");
  const del = async (id) => { if (!window.confirm("Hapus invoice ini?")) return; await api.delete(`/admin/invoices/${id}`); toast.success("Invoice dihapus"); if (inv.id === id) setInv(EMPTY()); load(); };
  const saveGset = async () => {
    setSavingGset(true);
    try { await api.put("/admin/settings/invoice", gset); toast.success("Pengaturan invoice tersimpan permanen"); }
    catch { toast.error("Gagal menyimpan pengaturan"); } finally { setSavingGset(false); }
  };
  const quickStatus = async (id, v) => {
    setList((l) => l.map((x) => (x.id === id ? { ...x, shipping_status: v } : x)));
    if (inv.id === id) set("shipping_status", v);
    try { await api.put(`/admin/invoices/${id}/shipping-status`, { shipping_status: v }); toast.success("Status diperbarui"); }
    catch { toast.error("Gagal mengubah status"); load(); }
  };

  const renderCanvas = () => html2canvas(document.getElementById("invoice-print"), { scale: 3, backgroundColor: "#ffffff", useCORS: true });
  const makePdf = (canvas) => {
    const img = canvas.toDataURL("image/jpeg", 0.97);
    const pdf = new jsPDF({ unit: "px", format: [canvas.width, canvas.height], compress: true });
    pdf.addImage(img, "JPEG", 0, 0, canvas.width, canvas.height);
    return pdf;
  };
  const fileName = (ext) => `${inv.invoice_number || "invoice"}.${ext}`;
  const downloadBlob = (blob, name) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = name; a.click();
    setTimeout(() => URL.revokeObjectURL(url), 5000);
  };
  const exportAs = async (type) => {
    setBusy(true);
    try {
      const canvas = await renderCanvas();
      if (type === "jpg") {
        const a = document.createElement("a"); a.href = canvas.toDataURL("image/jpeg", 0.97); a.download = fileName("jpg"); a.click();
      } else {
        makePdf(canvas).save(fileName("pdf"));
      }
      toast.success(`Invoice ${type.toUpperCase()} diunduh`);
    } catch (e) { toast.error("Gagal mengekspor"); } finally { setBusy(false); }
  };
  const shareAs = async (type) => {
    setBusy(true);
    try {
      const canvas = await renderCanvas();
      let file;
      if (type === "jpg") {
        const blob = await new Promise((r) => canvas.toBlob(r, "image/jpeg", 0.97));
        file = new File([blob], fileName("jpg"), { type: "image/jpeg" });
      } else {
        const blob = makePdf(canvas).output("blob");
        file = new File([blob], fileName("pdf"), { type: "application/pdf" });
      }
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: `Invoice ${inv.invoice_number || ""}` });
      } else {
        downloadBlob(file, file.name);
        toast.info("Perangkat tidak mendukung share langsung — file diunduh");
      }
    } catch (e) { if (e?.name !== "AbortError") toast.error("Gagal membagikan file"); } finally { setBusy(false); }
  };
  const card = "rounded-2xl border border-slate-200 bg-white p-5";

  const settingsCard = (
    <div className={`${card} border-l-4`} style={{ borderLeftColor: gset.theme_color || "#F0005A" }}>
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <h3 className="font-semibold text-[#00133F] flex items-center gap-2"><Settings2 className="w-4 h-4 text-[#F0005A]" /> Pengaturan Invoice <span className="text-xs font-normal text-slate-400">(default otomatis untuk semua invoice, tersimpan permanen)</span></h3>
        <Button size="sm" onClick={saveGset} disabled={savingGset} className="rounded-full h-8 bg-[#00133F] text-white" data-testid="inv-settings-save">{savingGset ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Simpan Pengaturan"}</Button>
      </div>
      <div className="flex flex-wrap gap-6 items-start">
        <ImgPick label="Logo Toko (default)" value={gset.logo_url} onChange={(v) => setGset((g) => ({ ...g, logo_url: v }))} testid="invset-logo-upload" />
        <ImgPick label="Tanda Tangan (default)" value={gset.signature_url} onChange={(v) => setGset((g) => ({ ...g, signature_url: v }))} testid="invset-signature-upload" />
        <div>
          <Label className="text-xs">Warna Tema (default)</Label>
          <div className="flex items-center gap-2 mt-1 flex-wrap">
            <input type="color" value={gset.theme_color || "#3b5bdb"} onChange={(e) => setGset((g) => ({ ...g, theme_color: e.target.value }))} className="w-10 h-10 rounded-lg border border-slate-200 cursor-pointer" data-testid="invset-theme-color" />
            {THEME_PRESETS.map((c) => (
              <button key={c} onClick={() => setGset((g) => ({ ...g, theme_color: c }))} className="w-7 h-7 rounded-full border-2 border-white shadow ring-1 ring-slate-200" style={{ background: c }} title={c} data-testid={`invset-theme-${c}`} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  if (!isEditor) {
    return (
      <div className="min-h-screen bg-slate-50">
        <div className="sticky top-0 z-20 bg-white border-b border-slate-200 px-4 py-3 flex items-center gap-2 flex-wrap">
          <a href="/admin" className="text-sm text-slate-500 hover:text-[#00133F] flex items-center gap-1 mr-2" data-testid="invoice-back"><ArrowLeft className="w-4 h-4" /> Dashboard</a>
          <span className="font-heading font-bold text-[#00133F] mr-auto flex items-center gap-2"><FileText className="w-5 h-5 text-[#F0005A]" /> Daftar Invoice</span>
          <Button size="sm" onClick={newInvoice} className="rounded-full bg-[#F0005A] text-white" data-testid="invoice-new-btn"><Plus className="w-4 h-4 mr-1" /> Baru</Button>
        </div>
        <div className="max-w-7xl mx-auto p-4 space-y-5">
          <InvoiceListView list={list} shipStatus={SHIP_STATUS} fmtDate={fmtDate} onOpen={(x) => setSp({ id: x.id })} onStatus={quickStatus} onDelete={del} />
          {settingsCard}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <style>{`@media print { body * { visibility: hidden !important; } #invoice-print, #invoice-print * { visibility: visible !important; } #invoice-print { position: absolute; left: 0; top: 0; width: 210mm; box-shadow: none !important; } .no-print { display: none !important; } }`}</style>

      {/* Toolbar */}
      <div className="no-print sticky top-0 z-20 bg-white border-b border-slate-200 px-4 py-3 flex items-center gap-2 flex-wrap">
        <a href="/admin/invoice" className="text-sm text-slate-500 hover:text-[#00133F] flex items-center gap-1 mr-2" data-testid="invoice-back-list"><ArrowLeft className="w-4 h-4" /> Daftar Invoice</a>
        <span className="font-heading font-bold text-[#00133F] mr-auto flex items-center gap-2"><FileText className="w-5 h-5 text-[#F0005A]" /> Invoice Pro {inv.invoice_number && <span className="text-xs font-normal text-slate-400">· {inv.invoice_number}</span>}</span>
        <Button size="sm" onClick={save} disabled={saving} className="rounded-full bg-[#F0005A] text-white" data-testid="invoice-save-btn">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-1" /> Simpan/Edit</>}</Button>
        <Button size="sm" variant="outline" onClick={() => setPreview(true)} className="rounded-full" data-testid="invoice-preview-btn"><Eye className="w-4 h-4 mr-1" /> Preview</Button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button size="sm" className="rounded-full bg-[#00133F] hover:bg-[#00133F]/90 text-white" disabled={busy} data-testid="invoice-export-btn">
              {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Download className="w-4 h-4 mr-1" />} Ekspor Invoice <ChevronDown className="w-3.5 h-3.5 ml-1" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" data-testid="invoice-export-menu">
            <DropdownMenuItem onClick={() => window.print()} data-testid="invoice-export-print"><Printer className="w-4 h-4 mr-2" /> Print</DropdownMenuItem>
            <DropdownMenuItem onClick={() => exportAs("pdf")} data-testid="invoice-export-pdf"><Download className="w-4 h-4 mr-2" /> Unduh PDF</DropdownMenuItem>
            <DropdownMenuItem onClick={() => exportAs("jpg")} data-testid="invoice-export-jpg"><Download className="w-4 h-4 mr-2" /> Unduh JPG</DropdownMenuItem>
            <DropdownMenuItem onClick={() => shareAs("pdf")} data-testid="invoice-share-pdf"><Share2 className="w-4 h-4 mr-2" /> Share PDF</DropdownMenuItem>
            <DropdownMenuItem onClick={() => shareAs("jpg")} data-testid="invoice-share-jpg"><Share2 className="w-4 h-4 mr-2" /> Share JPG</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Full-page form */}
      <div className="no-print max-w-6xl mx-auto p-4 space-y-5">
        <div className="grid lg:grid-cols-2 gap-5">
          <div className={`${card} space-y-3`}>
            <h3 className="font-semibold text-[#00133F]">Data Pembeli</h3>
            <div><Label className="text-xs">Nama Pembeli</Label><Input value={inv.buyer_name} onChange={(e) => set("buyer_name", e.target.value)} className="mt-1 rounded-xl" data-testid="inv-buyer-name" /></div>
            <div><Label className="text-xs">Alamat Pembeli</Label><Textarea value={inv.buyer_address} onChange={(e) => set("buyer_address", e.target.value)} className="mt-1 rounded-xl" rows={3} data-testid="inv-buyer-address" /></div>
            <div><Label className="text-xs">Tanggal Invoice</Label><Input type="date" value={inv.date} onChange={(e) => set("date", e.target.value)} className="mt-1 rounded-xl" /></div>
          </div>

          <div className={`${card} space-y-3`}>
            <h3 className="font-semibold text-[#00133F]">Pengiriman</h3>
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="text-xs">Ongkos Kirim</Label><Input type="number" value={inv.shipping_cost} onChange={(e) => set("shipping_cost", e.target.value)} className="mt-1 rounded-xl" data-testid="inv-shipping-cost" /></div>
              <div><Label className="text-xs">Kirim Via</Label><Input value={inv.shipping_via} onChange={(e) => set("shipping_via", e.target.value)} placeholder="JNE REG" className="mt-1 rounded-xl" /></div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="text-xs">Resi</Label><Input value={inv.shipping_resi} onChange={(e) => set("shipping_resi", e.target.value)} className="mt-1 rounded-xl" /></div>
              <div><Label className="text-xs">Tgl Pengiriman</Label><Input type="date" value={inv.shipping_date} onChange={(e) => set("shipping_date", e.target.value)} className="mt-1 rounded-xl" /></div>
            </div>
            <div>
              <Label className="text-xs">Status Pengiriman</Label>
              <Select value={inv.shipping_status} onValueChange={(v) => set("shipping_status", v)}>
                <SelectTrigger className="rounded-xl mt-1" data-testid="inv-shipping-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {SHIP_STATUS.map((s) => <SelectItem key={s.v} value={s.v}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        <div className={card}>
          <h3 className="font-semibold text-[#00133F] mb-2">Produk</h3>
          <datalist id="catalog-list">{products.map((p) => <option key={p.id} value={p.name} />)}</datalist>
          <div className="space-y-2">
            {inv.items.map((it, i) => (
              <div key={i} className="flex gap-2 items-center" data-testid={`inv-item-${i}`}>
                <input list="catalog-list" value={it.name} onChange={(e) => onItemName(i, e.target.value)} placeholder="Pilih / ketik produk" className="flex-1 min-w-0 h-10 px-3 rounded-xl border border-slate-200 text-sm outline-none focus:border-[#F0005A]" data-testid={`inv-item-name-${i}`} />
                <input type="number" value={it.qty} onChange={(e) => setItem(i, "qty", e.target.value)} className="w-16 h-10 px-1 text-center rounded-xl border border-slate-200 text-sm outline-none" data-testid={`inv-item-qty-${i}`} />
                <input type="number" value={it.price} onChange={(e) => setItem(i, "price", e.target.value)} placeholder="Harga" className="w-28 h-10 px-2 rounded-xl border border-slate-200 text-sm outline-none" data-testid={`inv-item-price-${i}`} />
                <span className="w-28 text-right text-sm font-medium text-slate-600 hidden sm:block">{formatRupiah((Number(it.qty) || 0) * (Number(it.price) || 0))}</span>
                <button onClick={() => removeItem(i)} className="text-slate-300 hover:text-red-500 p-1"><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
          <Button size="sm" variant="ghost" className="rounded-full h-8 text-xs mt-2" onClick={addItem} data-testid="inv-add-item"><Plus className="w-3 h-3 mr-1" /> Baris Produk</Button>
          <div className="flex justify-end gap-6 mt-3 text-sm border-t border-slate-100 pt-3">
            <span className="text-slate-500">Subtotal: <b className="text-[#00133F]">{formatRupiah(subtotal)}</b></span>
            <span className="text-slate-500">Total: <b className="text-[#F0005A]">{formatRupiah(grandTotal)}</b></span>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-5">
          <div className={`${card} space-y-3`}>
            <h3 className="font-semibold text-[#00133F]">Riwayat Pembayaran</h3>
            {inv.payments.length === 0 && <p className="text-xs text-slate-400">Belum ada pembayaran. Klik "Tambah Pembayaran" untuk mencatat DP / Pelunasan.</p>}
            <div className="space-y-2">
              {inv.payments.map((p, i) => (
                <div key={i} className="flex gap-2 items-center" data-testid={`inv-payment-${i}`}>
                  <input value={p.label} onChange={(e) => setPay(i, "label", e.target.value)} placeholder="DP / Pelunasan" className="flex-1 min-w-0 h-10 px-3 rounded-xl border border-slate-200 text-sm outline-none focus:border-[#F0005A]" data-testid={`inv-payment-label-${i}`} />
                  <input type="date" value={p.date} onChange={(e) => setPay(i, "date", e.target.value)} className="w-36 h-10 px-2 rounded-xl border border-slate-200 text-sm outline-none" data-testid={`inv-payment-date-${i}`} />
                  <input type="number" value={p.amount} onChange={(e) => setPay(i, "amount", e.target.value)} placeholder="Jumlah" className="w-28 h-10 px-2 rounded-xl border border-slate-200 text-sm outline-none" data-testid={`inv-payment-amount-${i}`} />
                  <button onClick={() => removePay(i)} className="text-slate-300 hover:text-red-500 p-1"><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
            </div>
            <Button size="sm" variant="ghost" className="rounded-full h-8 text-xs" onClick={addPay} data-testid="inv-add-payment"><Plus className="w-3 h-3 mr-1" /> Tambah Pembayaran</Button>
            <div className="border-t border-slate-100 pt-3 space-y-1 text-sm">
              <div className="flex justify-between"><span className="text-slate-500">Total Dibayar</span><b className="text-[#00133F]" data-testid="inv-paid-total">{formatRupiah(paidTotal)}</b></div>
              <div className="flex justify-between"><span className="text-slate-500">Sisa Tagihan</span><b className={balance === 0 ? "text-emerald-600" : "text-[#F0005A]"} data-testid="inv-balance-due">{formatRupiah(balance)}</b></div>
              <div className="flex justify-between items-center"><span className="text-slate-500">Status Bayar</span>
                <span className={`text-xs font-semibold px-2.5 py-0.5 rounded-full ${payStatus === "paid" ? "bg-emerald-100 text-emerald-700" : payStatus === "dp" ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-500"}`} data-testid="inv-pay-status">{PAY_LABEL[payStatus]}</span>
              </div>
            </div>
          </div>

          <div className={`${card} space-y-3`}>
            <h3 className="font-semibold text-[#00133F]">Pembayaran & Ketentuan</h3>
            <div><Label className="text-xs">Cara Pembayaran</Label><Textarea value={inv.payment_method} onChange={(e) => set("payment_method", e.target.value)} rows={3} className="mt-1 rounded-xl" /></div>
            <div><Label className="text-xs">Syarat & Ketentuan</Label><Textarea value={inv.terms} onChange={(e) => set("terms", e.target.value)} rows={4} className="mt-1 rounded-xl" /></div>
          </div>
        </div>

        <div className={card}>
          <h3 className="font-semibold text-[#00133F] mb-3">Branding Invoice</h3>
          <div className="flex flex-wrap gap-6 items-start">
            <ImgPick label="Logo (kosongkan = ikuti Pengaturan Invoice)" value={inv.logo_url} onChange={(v) => set("logo_url", v)} testid="inv-logo-upload" />
            <ImgPick label="Tanda Tangan (kosongkan = ikuti Pengaturan)" value={inv.signature_url} onChange={(v) => set("signature_url", v)} testid="inv-signature-upload" />
            <div className="flex-1 min-w-[200px]">
              <Label className="text-xs">Nama Tanda Tangan</Label>
              <Input value={inv.signature_name} onChange={(e) => set("signature_name", e.target.value)} className="mt-1 rounded-xl" data-testid="inv-signature-name" />
              <Label className="text-xs mt-3 block">Warna Tema (kosongkan = ikuti Pengaturan)</Label>
              <div className="flex items-center gap-2 mt-1 flex-wrap">
                <input type="color" value={inv.theme_color || gset.theme_color || accent || "#3b5bdb"} onChange={(e) => set("theme_color", e.target.value)} className="w-10 h-10 rounded-lg border border-slate-200 cursor-pointer" data-testid="inv-theme-color" />
                {THEME_PRESETS.map((c) => (
                  <button key={c} onClick={() => set("theme_color", c)} className="w-7 h-7 rounded-full border-2 border-white shadow ring-1 ring-slate-200" style={{ background: c }} title={c} data-testid={`inv-theme-${c}`} />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Preview modal */}
      <Dialog open={preview} onOpenChange={setPreview}>
        <DialogContent className="max-w-[96vw] w-[880px] max-h-[92vh] overflow-y-auto p-3 bg-slate-100" data-testid="invoice-preview-modal">
          <DialogHeader><DialogTitle className="text-[#00133F]">Preview Invoice</DialogTitle><DialogDescription className="sr-only">Pratinjau dokumen invoice sebelum dicetak atau diunduh</DialogDescription></DialogHeader>
          <div className="flex justify-center">
            <div className="origin-top scale-[0.62] sm:scale-75 lg:scale-90">
              <InvoiceDoc inv={inv} store={store} defLogo={logo} accent={accent} gset={gset} />
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Hidden render for print/export when preview closed */}
      {!preview && (
        <div aria-hidden className="fixed top-0 pointer-events-none" style={{ left: "-10000px" }}>
          <InvoiceDoc inv={inv} store={store} defLogo={logo} accent={accent} gset={gset} />
        </div>
      )}
    </div>
  );
}
